<template>
  <div class="detail-table-pro">
    <a-empty
      v-if="!hasConditionValue"
      class="dtp-center"
      :description="emptyDescription"
    />
    <tablePageHost
      v-else
      class="dtp-host"
      :app_flag="app_flag"
      :modalType="extra.modalType"
      :inject_conditions="conditions"
      :inherit_route_params="false"
      :use_store="false"
      :enable_ai_abilities="false"
      :enable_hash_sync="false"
      :enable_btn_add="!!extra.enable_btn_add"
      :enable_view_panel="!!extra.enable_view_panel"
      :view_key_namespace="extra.view_key_namespace || 'detail-remotePagesV2'"
      :title_inject="extra.title || ''"
      :setting_drawer_width="extra.setting_drawer_width || 800"
      :enable_pagination="paginationOverride"
      :enable_advanced_search_multigroups="!!extra.enable_advanced_search_multigroups"
      :hide_header="hideHeaderOverride"
      :search_display_type="extra.search_display_type || 'hide_and_show_summary'"
      :inject_form_defaults="formDefaults"
      :depth="depth ?? 1"
      :listen_global_show_detail="false"
      :max_depth="nestedMaxDepth"
    ></tablePageHost>
  </div>
</template>
<script setup lang="ts">
// 通用一对多详情表：按 extra.modalType 复用 remotePages 的页面配置，
// 用 extra.filter_list 把当前行 record 的值转成目标表的 conditions 预过滤。
// 壳逻辑（配置拉取/总线/详情抽屉/视图模式）全部由 tablePageHost.vue 提供，本组件只做：
//   1) extra 解析；
//   2) 过滤条件构建 + 空值闸门（取不到关联值时展示空态、不挂载表格、不发请求）；
//   3) 嵌套参数收敛（depth 由上层 Host 的详情渲染传入，恒不听全局总线、不写 hash、不注册 AI 能力）。
import { computed } from 'vue';
import tablePageHost from '../remotePages/v2/tablePageHost.vue';
import type { DetailTableProExtra } from '../remotePages/typing.js';

interface Props {
  cmpObj?: any; // 上层 Host 透传：{ record }（或含 column 等的单元格 cmpObj）
  extra?: DetailTableProExtra;
  depth?: number; // 嵌套层级：上层 Host 抽屉渲染时传 depth+1
}
const props = withDefaults(defineProps<Props>(), {
  cmpObj: null,
  extra: null,
  depth: undefined,
});

const app_flag = computed(() => (props.extra?.modalType || '').split('-')[0] || '');
// modalType 约定 `${app_flag}-${model_name}`，与 getUrl / obtain_<model>_column_data 同规则

// ---- 过滤条件构建 + 空值闸门 ----
const emptyDescription = computed(() => {
  const filter_list = props.extra?.filter_list || [];
  if (!props.extra?.modalType) {
    return '未配置目标表 modalType，请检查详情配置';
  }
  if (!filter_list.length) {
    return '未配置过滤条件对应关系，请检查详情配置';
  }
  return '缺少关联字段值，无法加载关联数据';
});

// 把 filter_list 转成 advanced 条件条目（field_name/operator/value/format/class_name）。
// fixed_value 优先（固定字面值，不依赖源行）；否则取 record[source_field]。
const conditions = computed(() => {
  const record = props.cmpObj?.record;
  const filter_list = props.extra?.filter_list || [];
  if (!filter_list.length) {
    return [];
  }
  const res: any[] = [];
  filter_list.forEach(item => {
    let value: any;
    if (item.fixed_value !== undefined && item.fixed_value !== null && item.fixed_value !== '') {
      value = coerceFixedValue(item.fixed_value);
    } else if (record) {
      value = record[item.source_field];
    }
    // 空值闸门：取不到值的条件直接丢弃；全部取不到时由 hasConditionValue 拦截（防退化为全表查询）
    if (value === undefined || value === null || value === '') {
      return;
    }
    res.push({
      field_name: item.target_field,
      operator: item.operator || 'eq',
      value: Array.isArray(value) ? value : [value],
      format: item.format || 'input',
      class_name: item.class_name || 'str',
    });
  });
  return res;
});
const hasConditionValue = computed(() => conditions.value.length > 0);

// 「新建」预置外键：把 filter_list 中按源行取值的项（target_field = record[source_field]）
// 作为目标表「新建」表单的初始值，实现「在此主表下新建子表数据」；固定值条件不参与预置
const formDefaults = computed(() => {
  const record = props.cmpObj?.record;
  const filter_list = props.extra?.filter_list || [];
  if (!record) {
    return {};
  }
  const res: Record<string, any> = {};
  filter_list.forEach(item => {
    if (item.fixed_value !== undefined && item.fixed_value !== null && item.fixed_value !== '') {
      return;
    }
    const value = record[item.source_field];
    if (value === undefined || value === null || value === '') {
      return;
    }
    res[item.target_field] = value;
  });
  return res;
});

// 配置界面落库的是字符串，这里按「true/false→布尔、纯数字→数值、其余→字符串」推测固定值类型
const coerceFixedValue = (raw: any) => {
  if (typeof raw !== 'string') {
    return raw;
  }
  const text = raw.trim();
  if (text === 'true') return true;
  if (text === 'false') return false;
  if (text !== '' && !isNaN(Number(text))) return Number(text);
  return raw;
};

// enable_nested_detail=false 时把 max_depth 压到本层（depth+1 > 1 ⇒ 更深层被拦截）
const nestedMaxDepth = computed(() => {
  if (props.extra?.enable_nested_detail === false) {
    return 1;
  }
  return props.extra?.max_depth ?? 2;
});

// 分页覆盖：extra.enable_pagination 为布尔时传给 Host 覆盖目标页面配置；未配置传 undefined（跟随目标页面）
const paginationOverride = computed(() =>
  typeof props.extra?.enable_pagination === 'boolean' ? props.extra.enable_pagination : undefined,
);

// 表头隐藏覆盖：extra.hide_header 为布尔时传给 Host 覆盖目标页面配置；未配置传 undefined（跟随目标页面，默认不隐藏）
const hideHeaderOverride = computed(() =>
  typeof props.extra?.hide_header === 'boolean' ? props.extra.hide_header : undefined,
);
</script>
<style scoped lang="less">
.detail-table-pro {
  height: 100%;
  overflow: hidden;
}
.dtp-center {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24px 0;
}
.dtp-host {
  height: 100%;
}
</style>
